'use client';

import type { ReactNode } from 'react';
import type { VideoUrlResult, VideoUrlRequestItem } from 'src/types/search-all/video-url';

import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import CircularProgress from '@mui/material/CircularProgress';

import { formatNumber } from 'src/utils/format/format-utils';

import { RADIUS, SHADOW, SURFACE } from 'src/theme/ui-tokens';
import { AI_CHAT_VISIBLE } from 'src/constants/ai-chat-visibility';
import { CARD_GRID_SINGLE_COLUMN_BELOW } from 'src/constants/card-grid';

import { Iconify } from 'src/components/iconify';
import { MediaBusy } from 'src/components/cmui/media-busy';
import { LoadingImg } from 'src/components/cmui/loading-img';
import { ChannelMarkIcon } from 'src/components/cmui/channel-tag';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { MatchSnippetText, matchSnippetTitle } from 'src/components/cmui/hub/match-snippet-text';

import { ScrubVideo } from './scrub-video';
import { MediaTextFace } from './media-text-face';
import { readAdRun } from '../utils/search-all-ad-run';
import { AI_CART_MAX } from '../context/use-search-all-ai-cart';
import { MEDIA_EMPHASIS_SX } from '../constants/search-all-media';
import { layoutJustifiedRows } from '../utils/search-all-justified';
import { MediaCardView, MediaListView, MediaTableView } from './media-views';
import { toRelativeLabel, toBookmarkTarget } from '../utils/search-all-media-cards';

import type { MediaView } from '../constants/search-all-media';
import type { MediaCard } from '../utils/search-all-media-cards';
import type { MatchSnippet } from '../utils/search-all-match-snippet';
import type { DetailHighlight } from '../utils/search-all-detail-options';

/**
 * 미디어 그리드 — 줄마다 높이를 맞추고 폭은 실물 비율대로 나누는 배치(justified rows).
 *
 * 실물은 자르지 않는다 — 이미지·영상의 가로세로 비율을 읽어 칸 비율로 쓴다(읽기 전에는 4:5). 이미지가 끝내 안 뜨는 카드는 접는다.
 * 광고 영상(수집 주소 · 만료되면 새로 받은 주소)과, 유효 주소를 받은 오가닉 영상(인스타그램·틱톡·유튜브 2분 미만)은 카드 안에서 바로 재생하고, 멈춰 있을 때 마우스로 장면을 훑는다(`ScrubVideo`). 배치는 줄마다 폭을 꽉 채우는 정렬 배치다(`layoutJustifiedRows`).
 * 맨 아래에 닿으면 다음 페이지를 부른다(`onReachEnd`). 버튼을 누르지 않는다.
 */

/** 한 줄의 목표 실물 높이(px) — 폭이 남으면 줄이 이만큼보다 조금 커진다 */
const ROW_HEIGHT = 500;
/** 좁은 화면(600px 미만)의 목표 실물 높이(px) */
const ROW_HEIGHT_NARROW = 320;
/** `perRow` 를 따르기 시작하는 폭(px) — 이보다 좁으면 줄당 장 수를 강요하지 않는다 */
const PER_ROW_MIN_WIDTH = 600;
/** 카드 사이 간격(px) */
const GAP = 8;
/** 한 줄 한 장(모바일 · `singleColumnNarrow`)의 실물 높이 상한 — 폭 × 이 값(4:5) */
const NARROW_MAX_HEIGHT_RATIO = 1.25;
/**
 * 영상을 **왜 못 트나** — 카드에 짧게 적는다. 풀리는 중이거나 이유를 모르면 빈 문자열이다.
 *
 * ⚠️ 여기 적히는 갈래는 **다시 물어도 안 온다** — 도는 표시로 두면 영영 도는 것처럼 보인다.
 *   다시 묻기를 다 쓴 건도 훅이 `unsupported`(`VIDEO_URL_GAVE_UP_REASON`)로 접어 「미리보기 없음」으로 온다.
 * ⚠️ 원문으로는 볼 수 있다 — 「못 본다」가 아니라 「여기서 미리보기가 안 된다」는 뜻이다.
 */
function videoNote(video: VideoUrlResult | undefined): string {
  if (!video) return '';
  if (video.status === 'unsupported') return video.reason === 'tooLong' ? '긴 영상' : '미리보기 없음';
  if (video.status === 'unavailable') return '원본 없음';
  return '';
}

/** 광고 CTA 줄 높이(px) — 오가닉 카드는 이만큼 실물을 더 키워 같은 줄 카드 높이를 맞춘다 */
export const CTA_HEIGHT = 30;
/** 검색어 조각 줄 높이(px) — 두 줄 · 모든 카드에 같게 둬서 같은 줄 카드 높이를 맞춘다 */
export const SNIPPET_HEIGHT = 44;
/** 카드 맨 아래 수·단추 줄의 최소 높이 */
const TILE_FOOTER_MIN = 34;
/**
 * 격자 **안에** 한 장으로 끼울 것 — 컨텐츠 허브의 전환 카드가 이 구멍을 쓴다.
 *
 * 🚨 **부르는 쪽이 넘길 때만 끼인다** — 이 격자는 허브·북마크·아이디어·공유 보기가 함께 쓴다
 *   (`HubMediaGrid`). 여기서 스스로 끼우면 그 화면들에도 다 나온다.
 * ⚠️ 비율을 **받아서** 줄 셈에 함께 넣는다 — 실물이 아니라 못 박은 수다. 안 넣으면 줄이 폭을
 *   못 채우고 이 한 장만큼 오른쪽이 빈다.
 */
export type MediaGridPromo = {
  /** 처음 끼우는 자리 — 몇 번째 카드 **앞**인가. 카드가 그보다 적으면 맨 끝에 한 장만 붙는다 */
  at: number;
  /**
   * 그 뒤로 **몇 장마다** 다시 끼울지 — 이어 불러오며 내려가도 계속 만난다.
   * ⚠️ 0 이하면 한 번만 끼운다 — 나머지 연산이 돌지 않는다.
   */
  every: number;
  /** 칸 비율(가로/세로) — 옆 카드와 같은 모양으로 두려면 0.8 이다 */
  ratio: number;
  node: ReactNode;
};

/** 끼워 넣은 장의 자리표 — 실물 카드 key 와 겹치지 않는 꼴이다(뒤에 몇 번째인지 붙는다) */
const PROMO_PREFIX = '__promo__';

/**
 * 이미지를 읽기 전의 자리 비율 — 채널마다 흔한 모양으로 잡아 읽은 뒤 줄이 덜 흔들리게 한다.
 * 틱톡은 세로 9:16, 유튜브 썸네일은 가로 16:9, 그 밖은 4:5. 이미지가 없는 글자 카드도 4:5.
 */
function defaultRatio(card: MediaCard): number {
  if (card.kind === 'product') return 1;
  if (!card.thumb) return 0.8;
  if (card.channel === 'tiktok') return 0.5625;
  if (card.channel === 'youtube') return 1.78;
  return 0.8;
}
/** 오가닉 카드가 화면 안에 이만큼(ms) 머물러야 영상 주소를 묻는다 — 빠른 스크롤로 스쳐 간 카드만 거른다 */
const VIDEO_DWELL_MS = 150;
const MIN_RATIO = 0.5;
const MAX_RATIO = 1.9;
/**
 * 배치에 쓰는 비율 상한 — 카드 폭은 줄 높이 × 이 값을 넘지 않는다.
 * 더 넓은 가로형 실물은 카드 안에 비율 그대로 담기고 위아래가 빈다(잘리지 않게).
 */
const LAYOUT_MAX_RATIO = 1.2;

/**
 * **어느 원장에서 온 카드인가** — 실물 오른쪽 위에 붙인다.
 *
 * 🔑 **머리 탭과 같은 말을 쓴다**(`TAB_LABEL`) — 「전체」로 훑을 때 세 원장이 섞여 오는데,
 *   카드만 보고는 광고인지 남의 글인지 판매몰 상품인지 가릴 수가 없었다.
 *   한 화면에서 같은 것을 두 이름으로 부르면 다른 것으로 읽힌다.
 * ⚠️ 셋 다 같은 모양이다 — 갈래는 **글자**가 말한다. 색으로 가르면 실물 위에 얹는 다른 표
 *   (재생 · 광고집행 · n/N)와 뜻이 섞인다.
 */
export const KIND_LABEL: Record<MediaCard['kind'], string> = {
  ad: 'META광고',
  organic: '소셜포스팅',
  product: '상품정보',
};

/** 갈래 배지가 먹는 폭(px) — 머리의 계정 이름이 그 밑으로 들어가지 않게 비워 둔다 */
export const KIND_BADGE_ROOM = 84;

const STAT_ICON: Record<MediaCard['stats'][number]['icon'], string> = {
  views: 'solar:play-linear',
  likes: 'solar:heart-linear',
  comments: 'solar:chat-round-dots-linear',
  rating: 'solar:star-linear',
  reviews: 'solar:chat-round-dots-linear',
};

function MediaTile({
  card,
  width,
  mediaHeight,
  wide,
  video,
  onRatio,
  onNeedVideo,
  onReleaseVideo,
  onVideoFail,
  onOpen,
  snippet,
  selected,
  selectFull,
  onToggleSelect,
  readOnly,
  selectable,
  overlay,
  emphasized = false,
}: {
  card: MediaCard;
  /** 배치가 정한 카드 폭(px) */
  width: number;
  /** 배치가 정한 줄 실물 높이(px) — 광고는 CTA 줄이 더해진다 */
  mediaHeight: number;
  /** 실물이 배치 비율 상한보다 넓은가 — 넓으면 잘라 채우지 않고 담는다(위아래 여백) */
  wide: boolean;
  /** 오가닉 영상의 유효 주소 결과 — 아직 없으면 `undefined` */
  video: VideoUrlResult | undefined;
  onRatio: (key: string, r: number) => void;
  onNeedVideo: (item: VideoUrlRequestItem) => void;
  /** 카드가 화면을 떠났다 — 그 주소를 다시 묻기를 멈춘다 */
  onReleaseVideo?: (id: string) => void;
  onVideoFail: (id: string, reason: string) => void;
  /** 카드 상세 드로워를 연다 */
  onOpen: (card: MediaCard) => void;
  /** 검색어에 걸린 조각 — `undefined` 면 줄을 두지 않고, `null` 이면 「확장 검색으로 찾음」 */
  snippet?: MatchSnippet | null;
  /** AI chat 에 담겼나 */
  selected: boolean;
  /** 더 담을 수 없나 — 담긴 카드는 그래도 뺄 수 있다 */
  selectFull: boolean;
  onToggleSelect: (card: MediaCard) => void;
  /** 읽기만 하는 자리인가 — 공유·북마크·담기를 감춘다(분석 화면의 썸네일 보기) */
  readOnly: boolean;
  /** AI 장바구니가 있는 자리인가 — 없으면 담기 단추만 감춘다(공유·북마크는 그대로) */
  selectable: boolean;
  /** 화면이 실물 위에 얹는 것 — 자리는 얹는 쪽이 정한다(`position: absolute`) */
  overlay: ReactNode;
  /** 찾은 말이 든 카드인가 — 켜면 테를 두른다(`MEDIA_EMPHASIS_SX`) */
  emphasized?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [collectedFailed, setCollectedFailed] = useState(false);
  const tile = useRef<HTMLDivElement | null>(null);

  // 글자 카드는 밝은 면이다 — 그 위에 얹는 머리도 어두운 글자로 뒤집는다
  const lightFace = !card.thumb;
  /** 이 글이 광고로 돌고 있나 — 색인 값만 본다(새 조회 없음) */
  const adRun = card.kind === 'organic' ? readAdRun(card.post) : null;
  const ask = card.videoAsk;
  // 수집 주소로 재생할 수 있으면 묻지 않는다 — 비었거나(만료) 재생에 실패했을 때만
  const collectedPlayable = Boolean(card.videoSrc) && !collectedFailed;
  // 화면 안에 VIDEO_DWELL_MS 이상 머문 카드만 주소를 묻는다 — 스크롤로 지나친 카드에는 호출이 나가지 않는다.
  // 화면을 떠나면 다시 묻기를 멈추고(`onReleaseVideo`), 돌아오면 다시 머문 뒤 이어 묻는다
  useEffect(() => {
    const node = tile.current;
    if (!node || !ask || collectedPlayable) return undefined;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let asked = false;
    const leave = () => {
      if (timer != null) clearTimeout(timer);
      timer = null;
      if (asked) onReleaseVideo?.(ask.id);
      asked = false;
    };
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible && timer == null && !asked) {
        timer = setTimeout(() => {
          timer = null;
          asked = true;
          onNeedVideo(ask);
        }, VIDEO_DWELL_MS);
      } else if (!visible) {
        leave();
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      leave();
    };
  }, [ask, collectedPlayable, onNeedVideo, onReleaseVideo]);

  const freshUrl = video?.status === 'ok' ? video.videoUrl : '';
  const playable = collectedPlayable ? card.videoSrc : freshUrl;

  // 새 결과가 오면 다시 재생을 시도한다 — 신고 뒤 같은 주소가 다시 와도 결과 객체가 바뀌므로 풀린다(주소 문자열만 보면 실패가 굳는다)
  useEffect(() => {
    setVideoFailed(false);
  }, [playable, video]);
  // 주소를 받는 중 — 실물 한가운데 받는 중 표시(`MediaBusy`)를 둔다. 영상이 붙으면 `ScrubVideo` 가 같은 자리를 이어받는다
  const waiting = Boolean(ask) && !playable && (video == null || video.status === 'pending' || video.status === 'stale');
  /** 담을 수 있는 카드인가 — 글·광고·상품 셋 다 담긴다(아니면 단추를 안 그린다) */
  const bookmarkTarget = toBookmarkTarget(card);

  const handleVideoFail = (reason: string) => {
    // 수집 주소가 막혔으면 새 주소를 묻는 쪽으로 넘어간다(위 효과가 다시 돈다)
    if (collectedPlayable) {
      setCollectedFailed(true);
      return;
    }
    setVideoFailed(true);
    if (ask && freshUrl) onVideoFail(ask.id, reason);
  };

  /**
   * 실물이 안 뜨는 카드 — **갈래에 따라 다르게 다룬다.**
   * 🚨 **글은 접는다**(실물이 전부라 빈 칸만 남는다 — 서명 만료·차단).
   * 🚨 **광고·상품은 접지 마라** — 글자가 본체다(브랜드·제목·가격·순위). 접으면 담아 둔 것이
   *   화면에서 사라져 「담았는데 안 보인다」가 된다(실측: 올리브영 상품이 폴더에서 통째로 빠졌다).
   *   대신 아래에서 **텍스트 얼굴**로 떨어뜨린다.
   */
  if (broken && card.kind === 'organic') return null;

  const fit = (w: number, h: number) => {
    if (w > 0 && h > 0) onRatio(card.key, Math.min(MAX_RATIO, Math.max(MIN_RATIO, w / h)));
  };

  return (
    <Card
      ref={tile}
      onClick={() => onOpen(card)}
      sx={{
        cursor: 'pointer',
        width,
        flexShrink: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        // 🚨 **글은 왼쪽부터다** — 물려받은 가운데 정렬에 걸리면 계정 이름만 카드 한가운데로 뜬다
        //    (게시글 탭이 그렇다 · `moment-content` 의 컨텐츠 영역이 가운데 정렬이다)
        textAlign: 'left',
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.raised,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        transition: (theme) => theme.transitions.create(['box-shadow', 'transform'], { duration: 150 }),
        '&:hover': { boxShadow: SHADOW.overlay, transform: 'translateY(-2px)' },
        ...(emphasized ? MEDIA_EMPHASIS_SX : null),
      }}
    >
      <Box sx={{ position: 'relative', height: card.kind !== 'organic' && (card.cta || card.landing) ? mediaHeight : mediaHeight + CTA_HEIGHT, bgcolor: card.kind === 'product' || lightFace ? 'common.white' : 'grey.900', overflow: 'hidden' }}>
        {/*
          🚨 **표지가 없으면 영상을 깔지 않는다** — 크롬은 표지 없는 `<video controls>` 를 프레임이 올 때까지
            **검은 상자**로 그린다(마우스를 올려야 받는다). 표지 미러가 빠진 영상 광고가 그 자리다
            (`mapAdCreative` 가 재생 주소만으로 살린 컷). 그때는 글자 얼굴로 떨어진다 — 영상은 상세에서 본다.
        */}
        {playable && !videoFailed && (card.thumb || video?.posterUrl) ? (
          <ScrubVideo
            src={playable}
            poster={card.thumb || video?.posterUrl || ''}
            /* 🔑 색인이 아는 길이 — 이것이 있으면 영상이 머리말을 미리 안 받아도 훑기가 선다 */
            durationSec={ask?.durationSec ?? null}
            onSize={fit}
            onFail={handleVideoFail}
            onSurfaceClick={() => onOpen(card)}
          />
        ) : (!card.thumb || broken) ? (
          <MediaTextFace card={card} topInset={52} />
        ) : (
          <Box title={card.title || undefined} sx={{ display: 'block', width: 1, height: 1 }}>
            <LoadingImg
              src={card.thumb}
              alt=""
              loading="lazy"
              onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => fit(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
              onError={() => setBroken(true)}
              sx={{ width: 1, height: 1, objectFit: wide || card.kind === 'product' ? 'contain' : 'cover', display: 'block' }}
            />
          </Box>
        )}

        {/* 머리는 실물 위에 얹는다 — 위쪽 어두운 그라데이션 위 흰 글자 */}
        <Stack
          direction="row"
          spacing={0.875}
          alignItems="center"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            pl: 1,
            // ⚠️ 오른쪽은 갈래 배지가 선다 — 비워 두지 않으면 계정 이름이 배지 밑으로 들어간다
            pr: `${KIND_BADGE_ROOM}px`,
            pt: 1,
            pb: 3,
            minWidth: 0,
            color: lightFace ? 'text.primary' : 'common.white',
            pointerEvents: 'none',
            background: lightFace
              ? 'linear-gradient(to bottom, rgba(255,255,255,0.95), rgba(255,255,255,0))'
              : 'linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))',
          }}
        >
          <Avatar src={card.avatar || undefined} sx={{ width: 36, height: 36, typography: 'uiBody', bgcolor: lightFace ? 'grey.300' : 'grey.600', color: lightFace ? 'text.primary' : 'common.white', border: lightFace ? '1.5px solid rgba(0,0,0,0.08)' : '1.5px solid rgba(255,255,255,0.7)' }}>
            {card.name.slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1, textShadow: lightFace ? 'none' : '0 1px 2px rgba(0,0,0,0.35)' }}>
            <Typography noWrap title={card.name} sx={{ typography: 'uiStrong', lineHeight: 1.35 }}>
              {card.name || '—'}
              {card.partner && <Box component="span" sx={{ fontWeight: 400, opacity: 0.85 }}> × {card.partner}</Box>}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, opacity: 0.9 }}>
              {card.kind === 'ad' && <Typography variant="caption" noWrap>{card.runLabel}</Typography>}
              {card.kind === 'product' && <Typography variant="caption" noWrap>{card.runLabel}</Typography>}
              {card.kind === 'organic' && (
                <Typography variant="caption" noWrap>
                  {toRelativeLabel(card.time)}
                  {card.followers != null && ` · 팔로워 ${formatNumber(card.followers)}`}
                </Typography>
              )}
              {card.channel && <ChannelMarkIcon channel={card.channel} />}
            </Stack>
          </Box>
        </Stack>

        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            px: 1,
            height: 20,
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: RADIUS.pill,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'common.white',
            typography: 'uiLabel',
            whiteSpace: 'nowrap',
          }}
        >
          {KIND_LABEL[card.kind]}
        </Box>

        {waiting && <MediaBusy label="영상을 받는 중" />}
        {/*
          ⚠️ **못 트는 이유를 말한다** — 원인이 뚜렷한데 재생 표시만 두면 「고장났나」로 읽힌다.
            2분 넘는 유튜브(`tooLong`)·대상 밖 채널(`channel`)·다시 묻기를 다 쓴 건은 **영영 안 온다** — 도는 표시를 두면 거짓이다.
          이 자리는 멈춘 표시만 둔다(▶ · 까닭) — 받는 중 표시는 위 `MediaBusy` 하나다.
        */}
        {card.video && !(playable && !videoFailed) && (
          <Tooltip title={videoNote(video) || ''} disableHoverListener={!videoNote(video)}>
            <Box sx={{ position: 'absolute', left: 8, bottom: 8, height: 28, minWidth: 28, px: videoNote(video) ? 1 : 0, borderRadius: RADIUS.pill, bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, typography: 'caption' }}>
              <Iconify icon="solar:play-circle-linear" width={18} />
              {videoNote(video) && <Box component="span" sx={{ typography: 'uiMeta', fontWeight: 600 }}>{videoNote(video)}</Box>}
            </Box>
          </Tooltip>
        )}
        {/*
          🔑 **돈이 들어간 글인지**를 실물 위에 적는다 — 훑을 때 오가닉 반응과 광고 반응이 섞이면
            「이 브랜드는 그냥 잘 되네」를 잘못 읽는다.
          ⚠️ 값이 없으면 **아무 표도 안 붙인다** — 「오가닉」이라 적으면 거짓이다(채널마다 붙은 비율이 다르다).
        */}
        {adRun && (
          <Tooltip title={adRun.hint}>
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                top: 8,
                px: 1,
                height: 20,
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: RADIUS.pill,
                bgcolor: adRun.kind === 'boost' ? 'warning.main' : 'rgba(0,0,0,0.6)',
                color: adRun.kind === 'boost' ? 'grey.900' : 'common.white',
                typography: 'uiLabel',
              }}
            >
              {adRun.label}
            </Box>
          </Tooltip>
        )}
        {card.slides > 1 && (
          <Box sx={{ position: 'absolute', right: 8, bottom: 8, px: 1, borderRadius: RADIUS.sm, bgcolor: 'rgba(0,0,0,0.55)', color: 'common.white', typography: 'caption', pointerEvents: 'none' }}>
            1/{card.slides}
          </Box>
        )}
        {overlay}
      </Box>

      {card.kind === 'product' && (card.cta || card.landing) && (
        <Stack
          component="a"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          href={card.href || undefined}
          target="_blank"
          rel="noopener noreferrer"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1, height: CTA_HEIGHT, bgcolor: 'text.primary', color: 'common.white', minWidth: 0, textDecoration: 'none' }}
        >
          {card.landing && <Typography variant="caption" sx={{ fontWeight: 600, color: 'error.light', flexShrink: 0 }}>{card.landing}</Typography>}
          <Typography variant="caption" noWrap sx={{ fontWeight: 600, flex: 1 }}>{card.cta}</Typography>
          <Iconify icon="solar:alt-arrow-right-linear" width={14} />
        </Stack>
      )}

      {card.kind === 'ad' && (card.cta || card.landing) && (
        <Stack
          component="a"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          href={card.landingUrl || card.href || undefined}
          target="_blank"
          rel="noopener noreferrer"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ px: 1, height: CTA_HEIGHT, bgcolor: 'text.primary', color: 'common.white', minWidth: 0, textDecoration: 'none' }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, flexShrink: 0 }}>{card.cta || '더 알아보기'}</Typography>
          <Typography variant="caption" noWrap sx={{ opacity: 0.7, flex: 1 }}>{card.landing}</Typography>
          <Iconify icon="solar:alt-arrow-right-linear" width={14} />
        </Stack>
      )}

      {snippet !== undefined && (
        <Typography
          variant="caption"
          component="div"
          title={matchSnippetTitle(snippet)}
          sx={{ px: 1, pt: 1, height: SNIPPET_HEIGHT, color: 'text.primary', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-all' }}
        >
          {/* 글머리(`lead`)는 찾는 말이 본문에 없을 때 — 허브처럼 «정규명으로 걸린» 자리에서 쓴다 */}
          <MatchSnippetText snippet={snippet} />
        </Typography>
      )}

      <Stack direction="row" alignItems="center" columnGap={1} flexWrap='wrap' sx={{ px: 1, py: 0.25, minHeight: TILE_FOOTER_MIN, mt: 'auto' }}>
        {card.lowImpressions && (
          <Tooltip title="Meta 광고 라이브러리 기준 노출 100회 미만">
            <Stack direction="row" spacing={0.1} alignItems="center" sx={{ color: 'text.secondary' }}>
              <Iconify icon="solar:eye-closed-linear" width={14} />
              <Typography variant="caption">&lt;100</Typography>
            </Stack>
          </Tooltip>
        )}
        {card.stats.map((st) => (
          <Stack key={st.icon} direction="row" spacing={0} alignItems="center">
            <Iconify icon={STAT_ICON[st.icon]} width={12} />
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', pl: 0.2 }}>{st.value}</Typography>
          </Stack>
        ))}
        <Box sx={{ flex: 1 }} />
        {/*
          🔑 **동작은 발치 한 묶음이다** — 담기·공유·북마크가 같은 자리에 선다. 담기만 컷 위
            오른쪽에 띄워 두었더니 한 카드의 일이 두 모서리로 갈렸다.
        */}
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, lineHeight: 0, '& > *': { display: 'flex', alignItems: 'center', lineHeight: 0 } }}>
          {!readOnly && selectable && (
            <Tooltip title={selected ? 'AI 에서 빼기' : selectFull ? `AI 에게는 ${AI_CART_MAX}개까지 담을 수 있습니다` : 'AI 에게 묻기'}>
              <Box
                component="span"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (!selected && selectFull) return;
                  onToggleSelect(card);
                }}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: RADIUS.pill,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: !selected && selectFull ? 'not-allowed' : 'pointer',
                  bgcolor: selected ? 'primary.main' : 'transparent',
                  color: selected ? 'common.white' : 'text.disabled',
                  opacity: !selected && selectFull ? 0.5 : 1,
                  '&:hover': { bgcolor: selected ? 'primary.dark' : SURFACE.hover },
                }}
              >
                {/* ⚠️ 화면 아래 안내가 「카드의 **+** 로 소재를 담거나」라고 말한다 — 아이콘이 그 말과 같아야 한다 */}
                <Iconify icon={selected ? 'solar:check-circle-linear' : 'solar:add-circle-linear'} width={14} />
              </Box>
            </Tooltip>
          )}
          {!readOnly && bookmarkTarget && (
            <Box onClick={(e) => e.stopPropagation()}>
              <PostCardBookmarkOverlay post={bookmarkTarget} placement="postTable" />
            </Box>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

export function MediaGrid({
  cards,
  loading,
  loadingMore,
  hasMore,
  videos,
  onReachEnd,
  onNeedVideo,
  onReleaseVideo,
  onVideoFail,
  onOpen,
  snippets,
  showSnippet,
  selectedKeys,
  selectFull,
  onToggleSelect,
  readOnly = false,
  endless = true,
  selectable = true,
  overlay,
  cardActions,
  singleColumnNarrow = false,
  perRow,
  maxRowHeight,
  view = 'thumb',
  promo,
  onOrderChange,
  highlight,
  emphasizedKeys,
}: {
  cards: MediaCard[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  /** 오가닉 영상 유효 주소 — 게시물 id 로 찾는다 */
  videos: Map<string, VideoUrlResult>;
  onReachEnd: () => void;
  onNeedVideo: (item: VideoUrlRequestItem) => void;
  /** 카드가 화면을 떠났다 — 그 주소를 다시 묻기를 멈춘다(없으면 계속 묻는다) */
  onReleaseVideo?: (id: string) => void;
  onVideoFail: (id: string, reason: string) => void;
  onOpen: (card: MediaCard) => void;
  /** 카드 key → 검색어에 걸린 문장 조각 */
  snippets: Map<string, MatchSnippet>;
  /** 검색어가 있을 때만 조각 줄을 둔다(없으면 줄째 뺀다) */
  showSnippet: boolean;
  /** AI chat 에 담긴 카드 key */
  selectedKeys: Set<string>;
  /** 더 담을 수 없나 */
  selectFull: boolean;
  onToggleSelect: (card: MediaCard) => void;
  /** 읽기만 하는 자리인가 — 공유·북마크·담기를 감춘다 */
  readOnly?: boolean;
  /** 아래로 이어 불러오는 자리인가 — `false` 면 발치의 「마지막 소재입니다」·감지선을 두지 않는다 */
  endless?: boolean;
  /**
   * AI 장바구니가 있는 자리인가 — `false` 면 **담기 단추만** 감춘다.
   * `readOnly` 와 다르다: 담을 곳은 없지만 북마크·공유는 여는 화면이 있다(컨텐츠 아이디어).
   * 🚨 AI 스위치(`AI_CHAT_VISIBLE`)가 꺼져 있으면 무엇을 주든 담기 단추는 네 보기 어디에도 없다.
   */
  selectable?: boolean;
  /** 카드마다 실물 위에 얹을 것 — **섬네일 보기에만** 얹힌다(카드·리스트·표에는 자리가 없다) */
  overlay?: (card: MediaCard) => ReactNode;
  /**
   * 부르는 화면이 칸마다 얹는 동작 — **네 보기 모두**에 선다(섬네일·카드는 실물 위 겹침 `'overlay'`,
   * 리스트·테이블은 줄 동작 자리 `'inline'`). 없으면 아무것도 안 그린다.
   */
  cardActions?: (card: MediaCard, place: 'overlay' | 'inline' | 'row') => ReactNode;
  /**
   * 격자 폭이 `PER_ROW_MIN_WIDTH` 미만이면 **한 줄에 한 장**을 폭 가득 세운다 — 실물은 4:5 높이까지만
   * 서고 넘치면 자른다(상품은 담는다).
   * 🔑 모바일(xs)은 이 값과 무관하게 늘 한 줄에 한 장이다(`CARD_GRID_SINGLE_COLUMN_BELOW`) — 이 값은
   *   sm 이상인데 격자가 좁은 자리(북마크 폴더)까지 넓힐 때만 켠다.
   */
  singleColumnNarrow?: boolean;
  /**
   * 넓을 때 한 줄에 몇 장을 세울지 — 없으면 목표 높이(`ROW_HEIGHT`)가 장 수를 정한다.
   * ⚠️ 못 박는 값이 아니다 — 카드 비율이 섞이면 한두 장 어긋난다(줄 폭은 늘 꽉 찬다).
   * ⚠️ **아주 넓은 화면에서는 장 수를 포기한다** — 셋이 폭을 나눠 가지면 세로 카드가 1,000px 을 넘는다.
   *   그때는 `maxRowHeight` 가 이겨 장 수가 늘어난다.
   */
  perRow?: number;
  /** 줄 높이 천장(px) — 넘으면 장 수를 늘려서라도 이 높이를 지킨다 */
  maxRowHeight?: number;
  /** 어떤 보기로 펼지 — 카드가 기본이고, 나머지 셋은 `media-views` 가 그린다 */
  view?: MediaView;
  /** 격자 안에 한 장으로 끼울 것 — **섬네일 보기에만** 끼인다(카드·리스트·표에는 자리가 없다) */
  promo?: MediaGridPromo;
  /**
   * 테이블이 편 순서(카드 key) — 상세의 이전/다음이 따른다.
   * ⚠️ **테이블 보기에서만** 온다 — 나머지 셋은 `cards` 순서 그대로 편다.
   */
  onOrderChange?: (keys: string[]) => void;
  /** 본문에 칠할 말 — 카드·리스트·테이블이 받는다(섬네일은 본문이 없어 조각 줄 `snippets` 가 그 몫이다) */
  highlight?: DetailHighlight | null;
  /** 테를 두를 카드 key — 네 보기 모두(표는 제 줄 바탕) */
  emphasizedKeys?: ReadonlySet<string>;
}) {
  const [ratios, setRatios] = useState<Record<string, number>>({});
  const [gridWidth, setGridWidth] = useState(0);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down(CARD_GRID_SINGLE_COLUMN_BELOW));
  /** 한 줄에 한 장인가 — 모바일이면 늘, 아니면 부르는 쪽이 켰을 때만 */
  const singleColumn = mobile || singleColumnNarrow;
  /** 담기 단추를 세우나 — 부르는 쪽의 `selectable` 과 AI 스위치가 둘 다 열려야 한다 */
  const pickable = selectable && AI_CHAT_VISIBLE;
  const sentinel = useRef<HTMLDivElement | null>(null);

  const handleRatio = useCallback((key: string, r: number) => {
    setRatios((prev) => (Math.abs((prev[key] ?? 0) - r) < 0.01 ? prev : { ...prev, [key]: r }));
  }, []);

  // 줄 폭은 그리드가 실제로 차지한 폭이다 — 창 크기·메뉴 접힘을 따라간다. 그리드는 카드가 생긴 뒤에야 붙어 콜백 ref 로 잡는다
  const resize = useRef<ResizeObserver | null>(null);
  const gridRef = useCallback((node: HTMLDivElement | null) => {
    resize.current?.disconnect();
    resize.current = null;
    if (!node) return;
    const observer = new ResizeObserver((entries) => setGridWidth(Math.floor(entries[0]?.contentRect.width ?? 0)));
    observer.observe(node);
    resize.current = observer;
  }, []);

  const rows = useMemo(() => {
    /* 🔑 끼워 넣은 장도 **줄 셈에 함께** 들어간다 — 평균 비율·장 수가 그대로 맞는다 */
    const promoRatio = promo ? Math.min(LAYOUT_MAX_RATIO, promo.ratio) : 0;
    const items: { key: string; ratio: number }[] = [];
    let promoCount = 0;
    cards.forEach((c, i) => {
      const due = promo && i >= promo.at && (promo.every > 0 ? (i - promo.at) % promo.every === 0 : i === promo.at);
      if (due) {
        items.push({ key: `${PROMO_PREFIX}${promoCount}`, ratio: promoRatio });
        promoCount += 1;
      }
      items.push({ key: c.key, ratio: Math.min(LAYOUT_MAX_RATIO, ratios[c.key] ?? defaultRatio(c)) });
    });
    /* ⚠️ 카드가 첫 자리보다 적을 때 — 그래도 한 장은 보여 준다(맨 끝에) */
    if (promo && promoCount === 0 && items.length > 0) {
      items.push({ key: `${PROMO_PREFIX}0`, ratio: promoRatio });
    }
    // 줄당 장 수를 받았으면 «평균 비율로 그만큼이 꽉 차는» 높이를 목표로 삼는다 — 줄은 여전히 폭을 꽉 채운다
    const averageRatio = items.length > 0 ? items.reduce((sum, it) => sum + it.ratio, 0) / items.length : 0.5625;
    const wide = gridWidth >= PER_ROW_MIN_WIDTH;
    if (singleColumn && !wide && gridWidth > 0) {
      return items.map((it) => ({
        height: Math.min(gridWidth / Math.max(it.ratio, MIN_RATIO), gridWidth * NARROW_MAX_HEIGHT_RATIO),
        complete: true,
        items: [{ key: it.key, width: gridWidth }],
      }));
    }
    // 줄당 장 수를 받았으면 «평균 비율로 그만큼이 꽉 차는» 높이를 목표로 삼는다
    const wanted = perRow && wide
      ? (gridWidth - GAP * (perRow - 1)) / (perRow * averageRatio)
      : (wide ? ROW_HEIGHT : ROW_HEIGHT_NARROW);
    const ceiling = maxRowHeight ?? Infinity;
    const target = Math.min(wanted, ceiling);
    // ⚠️ 목표 높이만으로는 장 수가 안 지켜진다(비율이 조금만 작아도 한 장이 더 들어간다) — 줄당 상한을 함께 건다.
    //   단 천장에 걸린 줄은 상한을 풀어 준다 — 안 그러면 줄이 폭을 못 채우고 오른쪽이 휑하게 빈다
    const capPerRow = Boolean(perRow) && wide && wanted <= ceiling;
    /**
     * 줄 높이 상한. 🚨 **장 수를 못 박은 줄에는 `target * 1.2` 를 걸지 마라** — 줄이 폭을 못 채우고
     * 오른쪽이 휑하게 빈다. 목표 높이는 **전체 평균 비율**로 잡는데, 아직 안 뜬 카드는 채널별 짐작값
     * (인스타 0.8)을 쓴다. 그래서 세로 영상(0.5625)만 모인 화면에서는 평균이 실제보다 납작해
     * 목표가 낮게 잡히고, 실제 줄은 그 1.2배에 걸려 잘린다(컨텐츠 아이디어가 그랬다).
     * 장 수를 못 박았으면 «줄은 폭을 꽉 채운다» 가 먼저이므로, 절대 천장(`maxRowHeight`)만 남긴다.
     */
    const rowMax = capPerRow ? ceiling : target * 1.2;
    return layoutJustifiedRows(items, gridWidth, target, GAP, rowMax, capPerRow && perRow ? perRow : 0);
  }, [cards, ratios, gridWidth, perRow, maxRowHeight, promo, singleColumn]);
  const byKey = useMemo(() => new Map(cards.map((c) => [c.key, c])), [cards]);

  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) onReachEnd();
    }, { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onReachEnd, cards.length]);

  if (loading && cards.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {Array.from({ length: 12 }, (_, i) => (
          <Skeleton key={i} variant="rounded" sx={{ flexGrow: 1, flexBasis: [240, 260, 220][i % 3], height: ROW_HEIGHT + 80 }} />
        ))}
      </Box>
    );
  }

  if (cards.length === 0) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">조건에 맞는 소재가 없습니다.</Typography>
      </Box>
    );
  }

  /**
   * **섬네일 말고는** 배치가 아주 달라 딴 부품이 그린다 — 이어 불러오기(`sentinel`)만 여기서 함께 붙인다.
   * 🔑 이 파일이 그리는 것이 **섬네일**이다 — 줄마다 폭을 꽉 채워 실물을 깔고 영상까지 튼다.
   *   카드·리스트·테이블은 **읽는 보기**라 `media-views` 가 맡는다.
   */
  if (view !== 'thumb') {
    const View = view === 'card' ? MediaCardView : view === 'list' ? MediaListView : MediaTableView;
    return (
      <>
        <View
          cards={cards}
          onOpen={onOpen}
          readOnly={readOnly}
          selectedKeys={selectedKeys}
          selectFull={selectFull}
          onToggleSelect={readOnly || !pickable ? undefined : onToggleSelect}
          onOrderChange={onOrderChange}
          cardActions={cardActions}
          highlight={highlight}
          emphasizedKeys={emphasizedKeys}
        />
        {hasMore && <Box ref={sentinel} sx={{ height: 1 }} />}
        {loadingMore && (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={22} />
          </Stack>
        )}
      </>
    );
  }

  return (
    <>
      <Box ref={gridRef} sx={{ display: 'flex', flexDirection: 'column', gap: `${GAP}px`, width: 1 }}>
        {rows.map((row) => (
          <Box key={row.items[0]?.key} sx={{ display: 'flex', gap: `${GAP}px`, alignItems: 'stretch' }}>
            {row.items.map(({ key, width }) => {
              /*
               * 끼워 넣은 한 장 — 줄이 정한 폭만 받고 높이는 줄에 맞춰 늘어난다(`alignItems: stretch`).
               * ⚠️ 줄에 혼자 남으면(소재가 적어 마지막 줄) 높이를 잡아 줄 옆 카드가 없다 — 같은 줄 카드 한 장의 높이를 바닥으로 건다
               */
              if (key.startsWith(PROMO_PREFIX)) {
                return (
                  <Box key={key} sx={{ width: `${width}px`, flex: 'none', minHeight: row.height + CTA_HEIGHT + (showSnippet ? SNIPPET_HEIGHT : 0) + TILE_FOOTER_MIN }}>
                    {promo?.node}
                  </Box>
                );
              }
              const card = byKey.get(key);
              if (!card) return null;
              return (
                <MediaTile
                  key={key}
                  card={card}
                  width={width}
                  mediaHeight={row.height}
                  wide={(ratios[key] ?? defaultRatio(card)) > LAYOUT_MAX_RATIO}
                  video={card.videoAsk ? videos.get(card.videoAsk.id) : undefined}
                  onRatio={handleRatio}
                  selected={selectedKeys.has(key)}
                  selectFull={selectFull}
                  onToggleSelect={onToggleSelect}
                  readOnly={readOnly}
                  selectable={pickable}
                  overlay={
                    overlay || cardActions ? (
                      <>
                        {overlay?.(card)}
                        {cardActions?.(card, 'overlay')}
                      </>
                    ) : null
                  }
                  snippet={showSnippet ? snippets.get(key) ?? null : undefined}
                  emphasized={emphasizedKeys?.has(key) ?? false}
                  onNeedVideo={onNeedVideo}
                  onReleaseVideo={onReleaseVideo}
                  onVideoFail={onVideoFail}
                  onOpen={onOpen}
                />
              );
            })}
          </Box>
        ))}
      </Box>
      {/*
        ⚠️ 「마지막」은 **이어 불러오는 자리에서만** 참이다 — 맛보기로 몇 장만 깐 자리에
        붙이면 1,691개 중 4개를 깔아 놓고 「마지막 소재입니다」라고 말하게 된다(`endless`).
      */}
      {endless && (
        <Box ref={sentinel} sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
          {loadingMore && <CircularProgress size={22} />}
          {!hasMore && <Typography variant="caption" color="text.disabled">마지막 소재입니다.</Typography>}
        </Box>
      )}
    </>
  );
}
